import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import { useSnackbar } from "notistack";
import {
  checkMoneyDataForTable,
  checkNonMoneyDataForTable,
  trimObject,
  dateTimeFormatter,
} from "utils/utils";
import { exportCsv, queryList } from "./model";
import { searchErrorHandler } from "utils/formValidation";
import s from "./OfflineTransactions.module.scss";
import FunctionalButtonBar from "components/FunctionalButtonBar/FunctionalButtonBar";
import Spin from "components/Spin/Spin";
import Datatable from "components/Datatable/Datatable";
import moment from "moment";
import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import Grid from "components/Grid/Grid";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import Selection from "components/Input/Selection/Selection";
import Datepicker from "components/Input/Datepicker/Datepicker";
import SearchFieldButtonBar from "components/SearchFieldButtonBar/SearchFieldButtonBar";
import Divider from "components/Divider/Divider";
import transactionTypeOptions from "utils/transactionTypeOptions/TransactionTypeOptions";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import cardTypeOptions from "utils/cardTypeOptions/CardTypeOptions.jsx";
import offlineTransactionStatusOptions from "utils/transactionStatusOptions/OfflineTransactionStatusOptions.js";
import paymentGatewayOptionsOperationOffline from "utils/paymentGatewayOptionsOperationOffline/PaymentGatewayOptionsOperationOffline.jsx";
import useTransactionActionDialogs from "../components/transaction-dialog-hook/useTransactionActionDialogs";
import TransactionDetailCard from "../components/transaction-detail-card/TransactionDetailCard";
import transactions from "routes/transactions";

const PAGE_LOCALE_KEY = "page.transactions.offlineTransactions.";

const INPUT_FIELD_INIT_STATE = () => ({
  id: "",
  transactionType: "",
  rrn: "",
  startDate: moment().subtract(1, "month").startOf("day").toISOString(),
  endDate: moment().endOf("day").toISOString(),
  cardType: "",
  fromAmount: "",
  toAmount: "",
  status: "",
  cardNo: "",
  merchantId: "",
  gateway: "",
  stan: "",
});

const getInputFieldOverrideStateExcept = (key) => {
  const defaultFields = {
    id: "",
    transactionType: "",
    rrn: "",
    startDate: null,
    endDate: null,
    cardType: "",
    fromAmount: "",
    toAmount: "",
    status: "",
    cardNo: "",
    merchantId: "",
    gateway: "",
    stan: "",
  };
  return Object.fromEntries(
    Object.entries(defaultFields).filter(([item]) => item !== key),
  );
};

const ERROR_INIT_STATE = {
  startDate: false,
  endDate: false,
  invalidDateRange: false,
  tooLargeDateRange: false,
  fromAmount: false,
  toAmount: false,
  invalidAmountRange: false,
};

const TRANSACTION_DETAIL_DIALOG_INIT_STATE = {
  transactionType: "",
  id: "",
  status: "",
  amount: 0,
  time: "",
  responseCode: "",
  terminalMid: "",
  terminalTid: "",
  first6Last4: "",
  aid: "",
  entryMode: "",
  rrn: "",
  paymentGateway: "",
  stan: "",
  authCode: "",
  batchNumber: "",
  description: "",
};

const OfflineTransactions = ({ redirectFn }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const gridId = "OfflineTransactionsListGrid";

  const { TransactionActionDialogComponents } =
    useTransactionActionDialogs({ env: "offline", dialogs: ["receipt"] });

  const [collapse, setCollapse] = useState(true);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [exporting, setExporting] = useState(false);
  const isInitRef = useRef(true);

  const [datasource, setDatasource] = useState({
    data: [],
    page: 0,
    total: 0,
  });
  const [search, setSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const pageSizeInfoRef = useRef({
    currentpage: 0,
    pagesize: 20,
  });

  const [searchFieldInput, setSearchFieldInput] = useState(
    INPUT_FIELD_INIT_STATE(),
  );
  const [focus, setFocus] = useState({});
  const [error, setError] = useState(ERROR_INIT_STATE);
  const [inputtingField, setInputtingField] = useState("");

  const [columnsArray, setColumnsArray] = useState([]);

  const [isTransactionDetailDialogOpen, setIsTransactionDetailDialogOpen] =
    useState(false);
  const [transactionDetailDialogData, setTransactionDetailDialogData] =
    useState(TRANSACTION_DETAIL_DIALOG_INIT_STATE);

  const useInputField = (fieldName, inputFieldsOverrideExcept) => {
    useEffect(() => {
      if (searchFieldInput[fieldName] !== "" && inputtingField !== fieldName) {
        setInputtingField(fieldName);
        setSearchFieldInput({
          ...searchFieldInput,
          ...inputFieldsOverrideExcept(fieldName),
        });
      } else if (searchFieldInput[fieldName] === "" && inputtingField === fieldName) {
        setInputtingField("");
        setSearchFieldInput(INPUT_FIELD_INIT_STATE());
      }
    }, [searchFieldInput[fieldName]]);
  };

  ["id"].forEach((field) => {
    useInputField(field, getInputFieldOverrideStateExcept);
  });

  useEffect(() => {
    let isOudated = false;
    const getIsOudated = () => isOudated;

    initialFetchList(getIsOudated);

    return () => {
      isOudated = true;
    };
  }, []);

  const initialFetchList = async (getIsOudated) => {
    setLoading(true);
    const apiResponse = await queryList(
      trimObject({
        page: pageSizeInfoRef.current.currentpage,
        size: pageSizeInfoRef.current.pagesize,
        ...INPUT_FIELD_INIT_STATE(),
        ...(isInitRef.current && { initPage: "true" }),
      }),
    );
    if (getIsOudated && getIsOudated()) return;

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            if (responseBody.data.length === 0) {
              snackbar(t("general.message.no.result"), "warning");
            }
            isInitRef.current = false;
            setDatasource(responseBody);
            setCurrentPage(responseBody.page);
            setPageSize(responseBody.size);
            pageSizeInfoRef.current.currentpage = responseBody.page;
            pageSizeInfoRef.current.pagesize = responseBody.size;
          } else {
            snackbar(t(PAGE_LOCALE_KEY + "fetchFail"), "error");
          }
        }
      }
    }

    setSearch(false);
    setLoading(false);
  };

  const resetInputField = () => {
    setSearchFieldInput(INPUT_FIELD_INIT_STATE());
  };

  const resetError = () => {
    setError(ERROR_INIT_STATE);
  };

  const handleSearchFieldCollapse = (status) => {
    setCollapse(status);
  };

  const handleReset = () => {
    isInitRef.current = true;
    setInputtingField("");
    resetInputField();
    resetError();
    initialFetchList();
  };

  const handleSearch = async (action) => {
    if (action === "search") {
      const { errorObj, hasError } = searchErrorHandler(searchFieldInput, [
        (input) =>
          ((input.startDate && !input.endDate) || input.endDate === "") && {
            endDate: true,
          },
        (input) =>
          ((input.endDate && !input.startDate) || input.startDate === "") && {
            startDate: true,
          },
        (input) =>
          moment(input.startDate).isAfter(input.endDate) && {
            invalidDateRange: true,
          },
        (input) => {
          const startDate = moment(input.startDate).startOf("day");
          const endDate = moment(input.endDate).startOf("day");
          if (endDate.diff(startDate, "month", true) > 6) {
            return { tooLargeDateRange: true };
          }
        },
        (input) => isNaN(input.toAmount) && { toAmount: true },
        (input) => isNaN(input.fromAmount) && { fromAmount: true },
        (input) => input.fromAmount && !input.toAmount && { toAmount: true },
        (input) => input.toAmount && !input.fromAmount && { fromAmount: true },
        (input) =>
          input.fromAmount &&
          input.toAmount &&
          !isNaN(input.fromAmount) &&
          !isNaN(input.toAmount) &&
          parseFloat(input.fromAmount) > parseFloat(input.toAmount) && {
            invalidAmountRange: true,
          },
      ]);
      setError(errorObj);
      if (hasError) return;

      setCurrentPage(0);
      pageSizeInfoRef.current.currentpage = 0;
    }
    setSearching(true);
    const params = trimObject({ ...searchFieldInput });
    const payload = {
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
      ...params,
    };
    const apiResponse = await queryList(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            if (responseBody.data.length === 0) {
              snackbar(t("general.message.no.result"), "warning");
            }
            setDatasource(responseBody);
            setCurrentPage(responseBody.page);
            setPageSize(responseBody.size);
            pageSizeInfoRef.current.currentpage = responseBody.page;
            pageSizeInfoRef.current.pagesize = responseBody.size;
          } else {
            snackbar(
              t(PAGE_LOCALE_KEY + "fetchFail") + ": " + responseBody.message,
              "error",
            );
          }
        }
      }
    }

    setSearch(true);
    setSearching(false);
  };

  const handleExportCsv = async () => {
    setExporting(true);
    const payload = trimObject({ ...searchFieldInput });
    const apiResponse = await exportCsv(payload);

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            snackbar(
              t("general.message.export.function.success.message"),
              "success",
            );
            const url = responseBody.data;
            window.location = url;
          } else {
            snackbar(
              t("general.message.export.function.fail.message"),
              "error",
            );
          }
        }
      }
    }
    setExporting(false);
  };

  // ===================== search field function start ===================== //
  const amountOnChange = (e, type, callback) => {
    if (type === "from") {
      setError({
        ...error,
        fromAmount: false,
        invalidAmountRange: false,
      });
      textInputOnChange(e, "fromAmount", callback);
    } else if (type === "to") {
      setError({ ...error, toAmount: false, invalidAmountRange: false });
      textInputOnChange(e, "toAmount", callback);
    } else {
      return;
    }
  };

  const textInputOnChange = (e, field, callback) => {
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setSearchFieldInput({ ...searchFieldInput, [field]: value });
  };

  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };

  const textInputOnBlur = (field, callback) => {
    if (searchFieldInput[field] && callback) {
      const isValidValue = callback(searchFieldInput[field]);
      if (!isValidValue) {
        setError({ ...error, [field]: error });
      }
    }
    setFocus({ [field]: false });
  };

  const isInputDisabled = (field) => {
    return ["id"].some(
      (item) => item !== field && searchFieldInput[item] !== "",
    );
  };

  const createdDateOnChange = (e, type) => {
    const rawDate = JSON.parse(JSON.stringify(e));
    let date = moment(rawDate).isValid()
      ? moment(rawDate)
      : e !== null
        ? ""
        : null;
    if (type === "start") {
      date = moment(rawDate).isValid()
        ? date.startOf("day").toISOString()
        : date;
      setError({
        ...error,
        startDate: false,
        invalidDateRange: false,
      });
      setSearchFieldInput({ ...searchFieldInput, startDate: date });
    } else if (type === "end") {
      date = moment(rawDate).isValid() ? date.endOf("day").toISOString() : date;
      setError({ ...error, endDate: false, invalidDateRange: false });
      setSearchFieldInput({ ...searchFieldInput, endDate: date });
    } else {
      return;
    }
  };
  // ===================== search field function end ===================== //

  // ===================== transaction detail dialog function ===================== //
  const openTransactionDetailDialog = (data) => {
    restoreTransactionDetailDialog();
    setTransactionDetailDialogData({
      ...data,
    });
    setIsTransactionDetailDialogOpen(true);
  };

  const closeTransactionDetailDialog = () => {
    setIsTransactionDetailDialogOpen(false);
  };

  const restoreTransactionDetailDialog = () => {
    setTransactionDetailDialogData(TRANSACTION_DETAIL_DIALOG_INIT_STATE);
  };

  const transactionDetailDialogContent = () => {
    return (
      <TransactionDetailCard
        offline
        dialog
        transaction={transactionDetailDialogData}
        openMainPayment={navigateToPaymentDetail}
        closeDialog={closeTransactionDetailDialog}
      />
    );
  };
  // ===================== transaction detail dialog function end ===================== //

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const dateFormatValidator = (dateValue) => {
    const isValidDateFormat = moment(dateValue).isValid();
    return isValidDateFormat;
  };

  const navigateToPaymentDetail = (id) => {
    const path = "/transactions/offline-payments-details";
    const payload = {
      name: transactions.find((tab) => tab.path === path).tabName + id,
      path: path,
      state: {
        id: id,
      },
    };
    redirectFn("redirectPage", payload);
  };

  const columns = [
    {
      key: "id",
      title: t(PAGE_LOCALE_KEY + "columnTitle.id"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "paymentId",
      title: t(PAGE_LOCALE_KEY + "columnTitle.paymentId"),
      sorter: false,
      render: (data, key) => {
        return (
          <div className={s.actionBtnContainer}>
            <ButtonMAS
              variant="text-only"
              onClick={(e) => {
                e.stopPropagation();
                navigateToPaymentDetail(data[key]);
              }}
            >
              {checkNonMoneyDataForTable(data, key)}
            </ButtonMAS>
          </div>
        );
      },
    },
    {
      key: "time",
      title: t(PAGE_LOCALE_KEY + "columnTitle.created"),
      sorter: false,
      render: (data, key) => {
        return dateTimeFormatter(checkNonMoneyDataForTable(data, key));
      },
    },
    {
      key: "merchantId",
      title: t(PAGE_LOCALE_KEY + "columnTitle.merchantId"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "cardType",
      title: t(PAGE_LOCALE_KEY + "columnTitle.cardType"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "transactionType",
      title: t(PAGE_LOCALE_KEY + "columnTitle.transactionType"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "amount",
      title: t(PAGE_LOCALE_KEY + "columnTitle.amount"),
      sorter: false,
      type: "amount",
      render: (data, key) => {
        return checkMoneyDataForTable(data, key);
      },
    },
    {
      key: "paymentGateway",
      title: t(PAGE_LOCALE_KEY + "columnTitle.paymentGateway"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "stan",
      title: t(PAGE_LOCALE_KEY + "columnTitle.stan"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "status",
      title: t(PAGE_LOCALE_KEY + "columnTitle.status"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "responseCode",
      title: t(PAGE_LOCALE_KEY + "columnTitle.respCode"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
  ].map((column, index) => ({ ...column, index: index }));

  const pagination = useMemo(() => {
    return {
      currentPage: currentPage,
      pageSize: pageSize,
      pageSizeOptions: [10, 20, 30, 40],
    };
  }, [currentPage, pageSize]);

  const searchField = () => {
    return (
      <div className={s.searchFieldContainer}>
        <Grid gridId={gridId} collapse={collapse}>
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.id.label")}
            value={searchFieldInput.id}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "id")}
            onfocusFn={() => textInputOnFocus("id")}
            onblurFn={() => textInputOnBlur("id")}
            sxParams={{ textFocus: focus.id }}
            disabled={isInputDisabled("id")}
          />
          <Selection
            label={t(PAGE_LOCALE_KEY + "search.transType.label")}
            selectionValue={searchFieldInput.transactionType}
            onchangeFn={(e) => textInputOnChange(e, "transactionType")}
            onfocusFn={() => textInputOnFocus("transactionType")}
            onblurFn={() => textInputOnBlur("transactionType")}
            options={transactionTypeOptions("all")}
            sxParams={{ selectionFocus: focus.transactionType }}
            disabled={isInputDisabled("transactionType")}
          />
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.rrn.label")}
            value={searchFieldInput.rrn}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "rrn")}
            onfocusFn={() => textInputOnFocus("rrn")}
            onblurFn={() => textInputOnBlur("rrn")}
            sxParams={{ textFocus: focus.rrn }}
            disabled={isInputDisabled("rrn")}
          />
          <Datepicker
            label={t(PAGE_LOCALE_KEY + "search.startDate.label")}
            errorMessage={
              error.invalidDateRange
                ? t(PAGE_LOCALE_KEY + "search.startDate.errorMessage.2")
                : error.tooLargeDateRange
                  ? t(PAGE_LOCALE_KEY + "search.startDate.errorMessage.3")
                  : t(PAGE_LOCALE_KEY + "search.startDate.errorMessage.1")
            }
            type="start"
            value={searchFieldInput.startDate}
            onchangeFn={createdDateOnChange}
            onfocusFn={() => textInputOnFocus("startDate")}
            onblurFn={() => textInputOnBlur("startDate", dateFormatValidator)}
            sxParams={{
              datepickerFocus: focus.startDate,
              datepickerError:
                error.startDate ||
                error.invalidDateRange ||
                error.tooLargeDateRange,
            }}
            disabled={isInputDisabled("startDate")}
          />
          <Datepicker
            label={t(PAGE_LOCALE_KEY + "search.endDate.label")}
            errorMessage={
              error.invalidDateRange
                ? t(PAGE_LOCALE_KEY + "search.endDate.errorMessage.2")
                : error.tooLargeDateRange
                  ? t(PAGE_LOCALE_KEY + "search.endDate.errorMessage.3")
                  : t(PAGE_LOCALE_KEY + "search.endDate.errorMessage.1")
            }
            type="end"
            value={searchFieldInput.endDate}
            onchangeFn={createdDateOnChange}
            onfocusFn={() => textInputOnFocus("endDate")}
            onblurFn={() => textInputOnBlur("endDate", dateFormatValidator)}
            sxParams={{
              datepickerFocus: focus.endDate,
              datepickerError:
                error.endDate ||
                error.invalidDateRange ||
                error.tooLargeDateRange,
            }}
            disabled={isInputDisabled("endDate")}
          />

          <Selection
            label={t(PAGE_LOCALE_KEY + "search.cardType.label")}
            selectionValue={searchFieldInput.cardType}
            onchangeFn={(e) => textInputOnChange(e, "cardType")}
            onfocusFn={() => textInputOnFocus("cardType")}
            onblurFn={() => textInputOnBlur("cardType")}
            options={cardTypeOptions("all")}
            sxParams={{ selectionFocus: focus.cardType }}
            disabled={isInputDisabled("cardType")}
          />
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.fromAmount.label")}
            errorMessage={
              error.invalidAmountRange
                ? t(PAGE_LOCALE_KEY + "search.fromAmount.errorMessage.2")
                : t(PAGE_LOCALE_KEY + "search.fromAmount.errorMessage.1")
            }
            value={searchFieldInput.fromAmount}
            onchangeFn={(e) => amountOnChange(e, "from")}
            onfocusFn={() => textInputOnFocus("fromAmount")}
            onblurFn={() => textInputOnBlur("fromAmount")}
            sxParams={{
              textFocus: focus.fromAmount,
              textError: error.fromAmount || error.invalidAmountRange,
            }}
            disabled={isInputDisabled("fromAmount")}
          />
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.toAmount.label")}
            errorMessage={
              error.invalidAmountRange
                ? t(PAGE_LOCALE_KEY + "search.toAmount.errorMessage.2")
                : t(PAGE_LOCALE_KEY + "search.toAmount.errorMessage.1")
            }
            value={searchFieldInput.toAmount}
            onchangeFn={(e) => amountOnChange(e, "to")}
            onfocusFn={() => textInputOnFocus("toAmount")}
            onblurFn={() => textInputOnBlur("toAmount")}
            sxParams={{
              textFocus: focus.toAmount,
              textError: error.toAmount || error.invalidAmountRange,
            }}
            disabled={isInputDisabled("toAmount")}
          />
          <Selection
            label={t(PAGE_LOCALE_KEY + "search.status.label")}
            selectionValue={searchFieldInput.status}
            onchangeFn={(e) => textInputOnChange(e, "status")}
            onfocusFn={() => textInputOnFocus("status")}
            onblurFn={() => textInputOnBlur("status")}
            options={offlineTransactionStatusOptions("all")}
            sxParams={{ selectionFocus: focus.status }}
            disabled={isInputDisabled("status")}
          />
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.cardNo.label")}
            value={searchFieldInput.cardNo}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "cardNo")}
            onfocusFn={() => textInputOnFocus("cardNo")}
            onblurFn={() => textInputOnBlur("cardNo")}
            sxParams={{ textFocus: focus.cardNo }}
            disabled={isInputDisabled("cardNo")}
          />
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.merchantId.label")}
            value={searchFieldInput.merchantId}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "merchantId")}
            onfocusFn={() => textInputOnFocus("merchantId")}
            onblurFn={() => textInputOnBlur("merchantId")}
            sxParams={{ textFocus: focus.merchantId }}
            disabled={isInputDisabled("merchantId")}
          />
          <Selection
            label={t(PAGE_LOCALE_KEY + "search.gateway.label")}
            selectionValue={searchFieldInput.gateway}
            onchangeFn={(e) => textInputOnChange(e, "gateway")}
            onfocusFn={() => textInputOnFocus("gateway")}
            onblurFn={() => textInputOnBlur("gateway")}
            options={paymentGatewayOptionsOperationOffline("all")}
            sxParams={{ selectionFocus: focus.gateway }}
            disabled={isInputDisabled("gateway")}
          />
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.stan.label")}
            value={searchFieldInput.stan}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "stan")}
            onfocusFn={() => textInputOnFocus("stan")}
            onblurFn={() => textInputOnBlur("stan")}
            sxParams={{ textFocus: focus.stan }}
            disabled={isInputDisabled("stan")}
          />
        </Grid>
        <SearchFieldButtonBar
          loading={loading || searching}
          collapse={collapse}
          handleCollapse={handleSearchFieldCollapse}
          resetBtn
          handleReset={handleReset}
          searchBtn
          handleSearch={handleSearch}
        />
        <Divider margin={32} />
      </div>
    );
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        {searchField()}
        <FunctionalButtonBar
          loading={loading}
          exporting={exporting}
          exportBtn
          handleExport={handleExportCsv}
          refreshBtn
          handleRefresh={handleSearch}
          columnDisplayBtn
          columnDisplayProps={{
            columns,
            columnsArray,
            setColumnsArray,
            rememberPreference: "transactions-offline-transactions",
          }}
        />
        <div
          className={[s.datatableContainer, loading ? s.loadingNow : ""].join(
            " ",
          )}
        >
          {loading ? (
            <Spin />
          ) : (
            // TODO column sorting
            <Datatable
              navigateToDetail={openTransactionDetailDialog}
              columns={columns}
              columnsPreference={columnsArray}
              datasource={datasource.data}
              total={datasource.total}
              pagination={pagination}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              pageSizeInfoRef={pageSizeInfoRef}
              handleSearch={handleSearch}
              search={search}
            />
          )}

          {searching && <Spin type={"search"} />}
        </div>
        {TransactionActionDialogComponents}
        <SimpleDialog
          open={isTransactionDetailDialogOpen}
          dialogOncloseFn={closeTransactionDetailDialog}
          title={t(PAGE_LOCALE_KEY + "transDialog.title")}
          content={transactionDetailDialogContent()}
        />
      </div>
    </div>
  );
};

export default React.memo(OfflineTransactions);
